import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Plus, Trash2, ChevronDown, ChevronRight, Image as ImageIcon, Search } from 'lucide-react'
import WizardModal, { inputCls, selectCls, labelCls } from './WizardModal'
import ImageCropField, { uploadImage } from './ImageCropField'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'
import { SEVERITIES, CHECKLIST_LANGS, CHECKLIST_CATEGORIES, checklistLang, checklistCatName } from '../equipment'

// Wizard de cadastro de Checklist pré-operacional (v2): 1) dados, 2) itens.
// As categorias são fixas (CHECKLIST_CATEGORIES): o item só escolhe uma delas,
// não há cadastro de categoria nem de nomes. Cada item tem código, categoria,
// severidade, textos em todos os idiomas suportados e uma foto própria (vai no
// asset pack como checklist/<code>.jpg). A versão é do servidor: ele incrementa
// sozinho quando os itens mudam.

let seq = 0
const uid = () => `it${++seq}`

const langMap = (src) => Object.fromEntries(CHECKLIST_LANGS.map(l => [l, (src || {})[l] || '']))

const cleanMap = (m) => {
  const out = {}
  for (const l of CHECKLIST_LANGS) {
    const v = (m?.[l] || '').trim()
    if (v) out[l] = v
  }
  return out
}

const anyText = (it) => CHECKLIST_LANGS.some(l => (it.texts[l] || '').trim() !== '')

// Checklist antigo (sem code por item): assume o mesmo fallback do backend (i1, i2, ...)
// para não trocar o nome da foto no pack de quem já tem o documento em cache.
const toFormItem = (it, i) => ({
  uid: uid(),
  raw: it,
  code: it.code || `i${i + 1}`,
  order: String(it.order ?? i + 1),
  category: it.category || '',
  blocker: !!it.blocker,
  severity: it.severity || (it.blocker ? 'A' : 'C'),
  active: it.active !== false,
  texts: langMap(it.texts && Object.keys(it.texts).length ? it.texts : { en: it.text || it.item || '' }),
  descriptions: langMap(it.descriptions),
  image: it.image_id ? { existingId: it.image_id, previewUrl: `/api/images/${it.image_id}` } : null,
})

const nextCode = (items) => {
  const nums = items.map(it => Number(it.code)).filter(n => Number.isFinite(n) && n > 0)
  return nums.length ? String(Math.max(...nums) + 1) : String(items.length + 1)
}

const nextOrder = (items) => {
  const nums = items.map(it => Number(it.order)).filter(n => Number.isFinite(n))
  return String((nums.length ? Math.max(...nums) : 0) + 1)
}

const dupCodes = (rows) => {
  const seen = new Set(), dup = new Set()
  for (const r of rows) {
    const c = (r.code || '').trim()
    if (!c) continue
    if (seen.has(c)) dup.add(c)
    seen.add(c)
  }
  return [...dup]
}

export default function ChecklistWizard({ open, onClose, editing = null }) {
  const { t } = useTranslation()
  const { reloadAdmin } = useWS()

  const editingItems = Array.isArray(editing?.items) ? editing.items : []

  const initial = {
    name: editing?.name || '',
    key: editing?.key || '',
    version: editing?.version || 1,
    categories: CHECKLIST_CATEGORIES,
    items: editingItems.map(toFormItem),
  }

  const steps = [
    {
      title: t('admin:checklists.wizard.step_data'),
      valid: f => f.name.trim() !== '',
      render: (f, set) => (
        <div className="space-y-4">
          <label className="block"><span className={labelCls}>{t('admin:checklists.wizard.name')}</span>
            <input value={f.name} onChange={e => set({ ...f, name: e.target.value })} placeholder={t('admin:checklists.wizard.name_ph')} className={inputCls} autoFocus />
          </label>
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <label className="block"><span className={labelCls}>{t('admin:checklists.wizard.key')}</span>
              <input value={f.key} readOnly={!!editing} onChange={e => set({ ...f, key: e.target.value })}
                placeholder={t('admin:checklists.wizard.key_ph')}
                className={inputCls + (editing ? ' bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400' : '')} />
            </label>
            <span title={t('admin:checklists.wizard.version_title')}
              className="mb-1 inline-flex items-center rounded-md bg-brand/10 text-brand-700 dark:text-brand-400 px-2 py-1 text-xs font-semibold tabular-nums">
              v{f.version}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('admin:checklists.wizard.key_hint')}</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('admin:checklists.wizard.intro')}</p>
        </div>
      ),
    },
    {
      title: t('admin:checklists.wizard.step_items'),
      valid: f => f.items.length > 0
        && f.items.every(it => it.code.trim() !== '' && anyText(it))
        && dupCodes(f.items).length === 0,
      render: (f, set) => <ItemsStep form={f} set={set} />,
    },
  ]

  const finish = async (f, setForm) => {
    const key = f.key.trim()
    const categories = CHECKLIST_CATEGORIES.map(c => ({ code: c.code, order: c.order }))

    // Controle de concorrência otimista: o documento pode ter sido salvo por outro
    // despachante enquanto este wizard estava aberto (149 itens levam tempo). O backend
    // recusa a escrita sobre versão velha com 409; a checagem daqui é para falhar ANTES
    // de subir as imagens dos itens, que ficariam órfãs no volume.
    if (editing?.id) {
      const cur = await fetch(`/api/checklists`).then(r => (r.ok ? r.json() : null)).catch(() => null)
      const server = Array.isArray(cur) ? cur.find(c => c.id === editing.id) : null
      if (server && Number(server.version || 1) !== Number(f.version || 1)) {
        throw new Error(t('admin:checklists.wizard.version_conflict', { version: server.version }))
      }
    }

    const items = []
    for (let i = 0; i < f.items.length; i++) {
      const it = f.items[i]
      const code = it.code.trim()
      let imageId = it.image?.existingId ?? null
      if (it.image?.blob) {
        const up = await uploadImage(it.image, 'checklist_item', `${key || f.name.trim()}:${code}`)
        imageId = up.id
        // Guarda o id já subido: se a gravação falhar, a tentativa seguinte não reenvia
        // a imagem (cada reenvio deixava um órfão no volume).
        setForm?.(prev => ({
          ...prev,
          items: prev.items.map((row, idx) => idx === i ? { ...row, image: { existingId: imageId } } : row),
        }))
      }
      const texts = cleanMap(it.texts)
      // o raw preserva campos que o wizard não gere (ex.: image_slot de checklists antigos)
      const row = {
        ...it.raw,
        code,
        order: Number(it.order) || i + 1,
        category: it.category,
        blocker: it.blocker,
        severity: it.blocker ? 'A' : it.severity,
        active: it.active !== false,
        text: texts.en || texts.pt || Object.values(texts)[0] || '',
        texts,
        descriptions: cleanMap(it.descriptions),
      }
      if (imageId != null) row.image_id = imageId
      else delete row.image_id
      items.push(row)
    }

    // version vai no corpo para o backend poder recusar uma escrita sobre versão velha.
    const body = { name: f.name.trim(), key, version: Number(f.version) || 1, categories, items }
    const url = editing ? `/api/checklists/${editing.id}` : '/api/checklists'
    const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    if (!res.ok) throw await apiError(res)
    reloadAdmin()
  }

  return (
    <WizardModal
      open={open}
      onClose={onClose}
      title={editing ? t('admin:checklists.edit') : t('admin:checklists.add')}
      icon={<ClipboardList size={18} className="text-brand" />}
      steps={steps}
      initial={initial}
      onFinish={finish}
      finishLabel={editing ? t('common:actions.save') : t('admin:actions.register')}
    />
  )
}

function ItemsStep({ form, set }) {
  const { t, i18n } = useTranslation()
  const [lang, setLang] = useState(checklistLang(i18n.language))
  const [catFilter, setCatFilter] = useState('')
  const [query, setQuery] = useState('')

  const items = form.items
  const setItem = (i, patch) => set({ ...form, items: items.map((it, idx) => idx === i ? { ...it, ...patch } : it) })
  const del = (i) => set({ ...form, items: items.filter((_, idx) => idx !== i) })
  const add = () => set({
    ...form,
    items: [...items, {
      uid: uid(), raw: {}, code: nextCode(items), order: nextOrder(items),
      category: catFilter || form.categories[0]?.code || '', blocker: false, severity: 'C', active: true,
      texts: langMap(null), descriptions: langMap(null), image: null,
    }],
  })

  const q = query.trim().toLowerCase()
  const rows = items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => (!catFilter || it.category === catFilter)
      && (!q || it.code.toLowerCase().includes(q)
        || (it.texts[lang] || '').toLowerCase().includes(q)
        || (it.descriptions[lang] || '').toLowerCase().includes(q)))

  const dups = dupCodes(items)
  const broken = items.map((it, i) => ({ it, i })).filter(({ it }) => it.code.trim() === '' || !anyText(it))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden" title={t('admin:checklists.wizard.lang_title')}>
          {CHECKLIST_LANGS.map(l => (
            <button key={l} type="button" onClick={() => setLang(l)}
              className={`px-2 py-1.5 text-[11px] font-semibold uppercase ${l === lang ? 'bg-brand text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              {l}
            </button>
          ))}
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className={selectCls + ' mt-0 w-40'}>
          <option value="">{t('admin:checklists.wizard.filter_all')}</option>
          {form.categories.map((c, ci) => <option key={ci} value={c.code}>{checklistCatName(c, lang)}</option>)}
        </select>
        <div className="relative flex-1 min-w-[10rem]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('admin:checklists.wizard.search_ph')}
            className={inputCls + ' mt-0 pl-7'} />
        </div>
        <button type="button" onClick={add} className="text-xs text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
          <Plus size={12} /> {t('admin:checklists.wizard.add_item')}
        </button>
      </div>

      <p className="text-[11px] text-slate-400 dark:text-slate-500">
        {t('admin:checklists.wizard.showing', { shown: rows.length, total: items.length })}
      </p>

      {rows.map(({ it, i }) => (
        <ItemRow key={it.uid} item={it} lang={lang} categories={form.categories}
          onPatch={patch => setItem(i, patch)} onDelete={() => del(i)} />
      ))}

      {items.length === 0 && (
        <button type="button" onClick={add}
          className="w-full flex flex-col items-center justify-center gap-1 h-24 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600">
          <Plus size={20} /> <span className="text-xs">{t('admin:checklists.wizard.add_item')}</span>
        </button>
      )}

      {dups.length > 0 && <p className="text-xs text-status-down">{t('admin:checklists.wizard.dup_codes', { codes: dups.join(', ') })}</p>}
      {broken.length > 0 && (
        <p className="text-xs text-status-down">
          {t('admin:checklists.wizard.invalid_items', { list: broken.map(({ it, i }) => it.code.trim() || `#${i + 1}`).join(', ') })}
        </p>
      )}
      <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('admin:checklists.wizard.help')}</p>
    </div>
  )
}

function ItemRow({ item, lang, categories, onPatch, onDelete }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  const catCodes = categories.map(c => c.code)
  const missingCat = item.category && !catCodes.includes(item.category)

  const setText = (v) => onPatch({ texts: { ...item.texts, [lang]: v } })
  const setDesc = (v) => onPatch({ descriptions: { ...item.descriptions, [lang]: v } })
  const toggleBlocker = (on) => onPatch({ blocker: on, severity: on ? 'A' : item.severity })

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-1.5 p-1.5">
        <button type="button" onClick={() => setOpen(o => !o)} className="text-slate-400 dark:text-slate-500 hover:text-brand-600 shrink-0"
          title={t(open ? 'admin:checklists.wizard.collapse' : 'admin:checklists.wizard.expand')}>
          <Chevron size={15} />
        </button>
        <input value={item.code} onChange={e => onPatch({ code: e.target.value })} title={t('admin:checklists.wizard.code')}
          className={inputCls + ' mt-0 w-16 px-2 py-1.5 font-mono tabular-nums'} />
        <input value={item.texts[lang] || ''} onChange={e => setText(e.target.value)} placeholder={t('admin:checklists.wizard.item_ph')}
          className={inputCls + ' mt-0 flex-1 px-2 py-1.5'} />
        <label className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 shrink-0 cursor-pointer"
          title={t('admin:checklists.wizard.blocker_title')}>
          <input type="checkbox" checked={item.blocker} onChange={e => toggleBlocker(e.target.checked)} className="accent-[#017f96]" />
          {t('admin:checklists.wizard.blocker_short')}
        </label>
        <select value={item.blocker ? 'A' : item.severity} onChange={e => onPatch({ severity: e.target.value })} disabled={item.blocker}
          title={t('admin:checklists.wizard.severity')}
          className="text-xs border border-slate-200 rounded-md px-1.5 py-1.5 bg-white text-slate-600 disabled:opacity-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
          {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.value}</option>)}
        </select>
        <button type="button" onClick={() => setOpen(true)} title={t('admin:checklists.wizard.image_label')}
          className="shrink-0 w-9 h-7 rounded border border-slate-200 dark:border-slate-700 overflow-hidden flex items-center justify-center text-slate-300 dark:text-slate-600 hover:border-brand/50">
          {item.image?.previewUrl
            ? <img src={item.image.previewUrl} alt="" className="w-full h-full object-cover" />
            : <ImageIcon size={13} />}
        </button>
        <button type="button" onClick={onDelete} className="text-slate-300 dark:text-slate-600 hover:text-status-down shrink-0"><Trash2 size={14} /></button>
      </div>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 p-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="block w-20"><span className={labelCls}>{t('admin:checklists.wizard.order')}</span>
                <input value={item.order} onChange={e => onPatch({ order: e.target.value })} inputMode="numeric" className={inputCls + ' tabular-nums'} />
              </label>
              <label className="block flex-1"><span className={labelCls}>{t('admin:checklists.wizard.category')}</span>
                <select value={item.category} onChange={e => onPatch({ category: e.target.value })} className={selectCls}>
                  <option value="">{t('admin:checklists.wizard.filter_none')}</option>
                  {categories.map((c, ci) => <option key={ci} value={c.code}>{checklistCatName(c, lang)}</option>)}
                  {missingCat && <option value={item.category}>{item.category}</option>}
                </select>
              </label>
            </div>
            <label className="block"><span className={labelCls}>{t('admin:checklists.wizard.description_label', { lang: lang.toUpperCase() })}</span>
              <textarea value={item.descriptions[lang] || ''} onChange={e => setDesc(e.target.value)} rows={3}
                placeholder={t('admin:checklists.wizard.description_ph')} className={inputCls + ' resize-y'} />
            </label>
          </div>
          <div className="space-y-1">
            <span className={labelCls}>{t('admin:checklists.wizard.image_label')}</span>
            <ImageCropField
              value={item.image}
              onChange={img => onPatch({ image: img })}
              aspect={4 / 3}
              hint={t('admin:checklists.wizard.image_hint')}
            />
          </div>
        </div>
      )}
    </div>
  )
}
